import React from 'react';
import { Card, CardContent, Box, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useParams } from 'react-router-dom';
import { useRouteNavigation } from '../../../hooks/useRouteNavigation';

interface ImportantLink {
  title: string;
  href: string;
  icon?: IconName;
  external?: boolean;
}

interface ImportantLinksCardProps {
  title: string;
  subtitle?: string;
  icon: IconName;
  links: ImportantLink[];
  color: string;
  lightColor: string;
}

export default function ImportantLinksCard({
  title,
  subtitle,
  icon,
  links,
  color,
  lightColor
}: ImportantLinksCardProps) {
  const navigateWithPrefix = useRouteNavigation();
  const params = useParams<{ state?: string; city?: string }>();

  const getHref = (link: ImportantLink) => {
    if (link.external) return link.href;

    // Gera o href com prefixo se estamos em uma rota com state/city
    const cleanPath = link.href.startsWith('/') ? link.href.substring(1) : link.href;
    if (params.state && params.city) {
      return `/${params.state}/${params.city}/${cleanPath}`;
    }
    return link.href;
  };

  const handleLinkClick = (link: ImportantLink) => (e: React.MouseEvent) => {
    if (!link.external) {
      e.preventDefault();
      navigateWithPrefix(link.href);
    }
    // Para links externos, deixa o comportamento padrão do navegador
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: 8,
        },
      }}
    >
      {/* Header com cor destacada */}
      <Box
        sx={{
          backgroundColor: color,
          color: 'white',
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon
            icon={icon}
            style={{
              fontSize: '1.8rem',
              color: 'white',
            }}
          />
        </Box>
        <Box>
          <Typography variant="h5" component="h3" fontWeight={700}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Lista de links */}
      <CardContent sx={{ flexGrow: 1, p: 0 }}>
        <List sx={{ py: 0 }}>
          {links.map((link, index) => (
            <ListItem
              key={index}
              disablePadding
              sx={{
                borderBottom: index < links.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <ListItemButton
                component="a"
                href={getHref(link)}
                target={link.external ? '_blank' : undefined}
                rel={link.external ? 'noopener noreferrer' : undefined}
                onClick={handleLinkClick(link)}
                sx={{
                  py: 2,
                  px: 3,
                  '&:hover': {
                    backgroundColor: lightColor + '20',
                    '& .MuiListItemIcon-root': {
                      transform: 'translateX(4px)',
                    },
                  },
                }}
              >
                {link.icon && (
                  <ListItemIcon sx={{ minWidth: 40, transition: 'transform 0.2s ease' }}>
                    <FontAwesomeIcon
                      icon={link.icon}
                      style={{
                        fontSize: '1.2rem',
                        color: color,
                      }}
                    />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={link.title}
                  primaryTypographyProps={{
                    fontWeight: 500,
                    fontSize: '0.95rem',
                  }}
                />
                {link.external ? (
                  <OpenInNewIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                ) : (
                  <ArrowForwardIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
}
